import React, {useEffect, useState} from 'react';
import {Modal, Button, Card, Icon} from '@salesforce/design-system-react';
import {StackSetsDeployment} from "../../../../utils/types/deployments/stacksets/deployment";
import {StackSetsDeploymentApiServiceFactory} from "../../../../services/deployment/stacksets/factory";
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";
import ToggleGuide from "../../../../components/pcsk_admin/deployment/stacksets/ToggleGuide";
import ChangeCaseInput from "../../../../utils/components/input/changeCaseInput";
import {extractCommitIDFromArtifact} from "../../../../components/pcsk_admin/deployment/stacksets/Utils";
import {GenericDeploymentSubmissionResponse} from "../../../../utils/types/deployments/genericDeploymentResponse";
import ArtifactSelector from "../../../../utils/components/input/artifactSelector";
import {ChangeCaseMetadata} from "../../../../utils/types/gus";
import ImplementationStepSelector from "../../../../utils/components/input/changeCaseImplementationSteps";
import ToggleWithPopover from "../../../../components/pcsk_admin/deployment/utils/toggles";
import ComplianceChecks from "../../../../components/pcsk_admin/deployment/utils/compliance";
import ThemedBadge from "../../../../utils/components/tag/tag";

interface RevertCommitModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedCommit: StackSetsDeployment;
    onRevertSuccess: (apiResponse: GenericDeploymentSubmissionResponse) => void;
}

const RevertStackSetCommitModal: React.FC<RevertCommitModalProps> = ({ isOpen, onClose, selectedCommit, onRevertSuccess }) => {
    const [revertArtifact, setRevertArtifact] = useState<string>('');
    const [changeCaseID, setChangeCaseID] = useState<string>('');
    const [isValidChangeCase, setIsValidChangeCase] = useState<boolean>(false);
    const [isRevertInProgress, setIsRevertInProgress] = useState<boolean>(false);
    const [disableRevertButton, setDisableRevertButton] = useState<boolean>(false);
    const [dryRun, setDryRun] = useState<boolean>(false);
    const [forceRevert, setForceRevert] = useState<boolean>(false);
    const [revertAPIResponse, setRevertAPIResponse] = useState<GenericDeploymentSubmissionResponse | undefined>(undefined);
    const [ccMetadata, setCCMetadata] = useState<ChangeCaseMetadata | null>(null);
    const [selectedImplStepID, setSelectedImplStepID] = useState<string>('');
    const [triggerComplianceCheck, setTriggerComplianceCheck] = useState<boolean>(false);
    const [lowerEnvDeploymentComplete, setLowerEnvDeploymentComplete] = useState<boolean>(true);

    const stackSetsApiService = StackSetsDeploymentApiServiceFactory.getService();

    useEffect(() => {
        setDisableRevertButton(isRevertInProgress || !isValidChangeCase || !validateCommitID(revertArtifact) || !isValidImplementationStep() || !lowerEnvDeploymentComplete)
    }, [isValidChangeCase, revertArtifact, isRevertInProgress, selectedImplStepID, lowerEnvDeploymentComplete]);
    useEffect(() => {
        setTriggerComplianceCheck( isValidChangeCase && isValidImplementationStep() && validateCommitID(revertArtifact))
    }, [isValidChangeCase, selectedImplStepID, revertArtifact]);


    const handleRevert = async () => {
        try {
            setIsRevertInProgress(true);
            const deploymentResponse = await stackSetsApiService.revertCommit(changeCaseID, selectedImplStepID, selectedCommit.commit_id, "Revert_" + selectedCommit.commit_title, extractCommitIDFromArtifact(revertArtifact), forceRevert, dryRun);
            deploymentResponse.success && onRevertSuccess(deploymentResponse)
            setRevertAPIResponse(deploymentResponse)
        } catch (error) {
            console.log("Error submitting commit for Revert", error);
            setRevertAPIResponse(new GenericDeploymentSubmissionResponse(`${error}`, false))
        } finally {
            setIsRevertInProgress(false);
        }
    };

    const disableImplStep = () => {
        return isRevertInProgress || !isValidChangeCase
    }
    const resetImplStep = () => {
        return !isValidChangeCase
    }
    const isValidImplementationStep = () => {
        return selectedImplStepID.length > 0
    }
    const disableComplianceChecks = () => {
        return isRevertInProgress || !isValidChangeCase || !isValidImplementationStep()
    }

    const validateCommitID = (commitID: string) => {
        return extractCommitIDFromArtifact(commitID).length > 0
    }

    const resetComplianceChecks = () => {
        return !isValidChangeCase ||  !isValidImplementationStep()
    }
    const resetArtifact = () => {
        return !isValidChangeCase || !isValidImplementationStep()
    }

    const disableArtifact = () => {
        return isRevertInProgress || !isValidChangeCase || !isValidImplementationStep()
    }

    const getBottomContent = () => {
        if (isRevertInProgress) {
            return <>
                <Card hasNoHeader={true}>
                    <div style={{minHeight: "10vh"}}>
                        <div className="slds-m-top_xx-large slds-flex slds-flex_column slds-align-items_center">
                            <CustomSpinnerOverlay size={"medium"}/>
                            <p className="slds-m-top_large slds-align_absolute-center slds-text-color--inverse-weak">
                                Submitting commit for revert
                            </p>
                        </div>
                    </div>
                </Card>
            </>
        }

        if (revertAPIResponse) {
            return <>
                <Card hasNoHeader={true}>
                    {revertAPIResponse.success ?
                        <div className="slds-m-around_medium slds-p-around_medium slds-box">
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_success">
                                Deployment Request Submitted Successfully!
                            </h2>
                        </div> :
                        <div className="slds-p-around_medium slds-box">
                            <div className="slds-text-wrap slds-size_1-of-1 slds-m-vertical--medium">
                                <ThemedBadge
                                    className="slds-m-right_medium"
                                    theme={"slds-theme--error"}
                                    text={"Deployment Request Could Not Be Processed!"}
                                />
                            </div>
                            {revertAPIResponse.errors.length > 0 &&
                                <ul className="slds-list_dotted">
                                    {revertAPIResponse.errors.map((msg, index) => (
                                        <li key={index} className={"slds-text-color_destructive"}>
                                            {msg}
                                        </li>
                                    ))}
                                </ul>
                            }
                        </div>
                    }
                </Card>
            </>
        }

        return <>
            <Card hasNoHeader={true}>
                <div className="slds-p-around_x-large"></div>
            </Card>
        </>
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            disableClose={isRevertInProgress}
            size="medium"
            dismissOnClickOutside={false}
            footer={
                <>
                    <Button label="Submit for Revert" onClick={handleRevert} variant="brand" disabled={disableRevertButton}/>
                </>
            }
        >
            <Card
                heading={<>
                    <strong className="slds-text-color_error"> Revert Commit: </strong>
                    <strong>{selectedCommit.commit_title}</strong>
                </>}
                icon={<Icon category="standard" name="actions_and_buttons" size="small"/>}
                className="slds-p-vertical_small slds-color__background_gray-3"
                bodyClassName={"slds-p-right_medium slds-p-left_medium"}
            >
                <p> {selectedCommit.commit_title || "No title provided"}</p>
            </Card>

            <div className="slds-grid slds-wrap">
                <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Developer: {selectedCommit.developer_id}
                </span>
                <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Commit ID: {selectedCommit.commit_id}
                </span>
                <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Status: {selectedCommit.deployment_status}
                </span>
            </div>

            <div className="slds-p-around_medium">
                <div className={"slds-box"}>
                    <div className="slds-grid slds-gutters slds-m-around_small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Change Case:</strong></label>
                        </div>
                        <div className="slds-col">
                            <ChangeCaseInput
                                isValidChangeCase={isValidChangeCase}
                                disableInput={isRevertInProgress}
                                changeCaseID={changeCaseID}
                                setChangeCaseID={setChangeCaseID}
                                setIsValidChangeCase={setIsValidChangeCase}
                                setCCMetadata={setCCMetadata}
                                deploymentType={"StackSetsTemplate"}
                            ></ChangeCaseInput>
                        </div>
                    </div>

                    <div className="slds-grid slds-gutters slds-m-around_small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Implementation Step:</strong></label>
                        </div>
                        <div className="slds-size_2-of-3">
                            <div className="slds-grid slds-grid_vertical-align-center">
                                <div className="slds-col">
                                    <ImplementationStepSelector
                                        changeCaseMetadata={isValidChangeCase ? ccMetadata :  null}
                                        setSelectedStep={setSelectedImplStepID}
                                        disable={disableImplStep()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="slds-grid slds-gutters slds-m-around_small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Revert to Artifact:</strong></label>
                        </div>
                        <div className="slds-col slds-size_2-of-3 slds-m-bottom--x-large">
                            <ArtifactSelector
                                setArtifact={setRevertArtifact}
                                disable={disableArtifact()}
                                prefix={"pcsk-stacksets"}
                                removeSelection={resetArtifact()}
                                filterContaining={[]} // allow revert to past commits
                            />
                        </div>
                    </div>

                    <div className="slds-p-around_medium slds-m-top--large">
                        <ToggleWithPopover
                            labelSuffix="Force Revert"
                            isChecked={forceRevert}
                            isDisabled={isRevertInProgress || !isValidChangeCase || !isValidImplementationStep() || !validateCommitID(revertArtifact)}
                            onToggle={(checked) => setForceRevert(checked)}
                            popoverHeading="Force Revert"
                            popoverBody="Overrides any ongoing stackset deployment."
                        />
                    </div>
                </div>
                {/*<div className={"slds-m-vertical--medium"}>*/}
                {/*    <ComplianceChecks*/}
                {/*        triggerComplianceCheck={triggerComplianceCheck}*/}
                {/*        disable={disableComplianceChecks()}*/}
                {/*        reset={resetComplianceChecks()}*/}
                {/*        commitID={selectedCommit.commit_id}*/}
                {/*        deploymentType={"StackSetsTemplate"}*/}
                {/*        artifactName={""}*/}
                {/*        setLowerEnvDeploymentComplete={setLowerEnvDeploymentComplete}*/}
                {/*    />*/}
                {/*</div>*/}
                {getBottomContent()}
            </div>
        </Modal>
    );
};

export default RevertStackSetCommitModal;
